import { Avatar, Box, Grid, Link, Stack, Typography } from '@mui/material';
import { ONE_DAY_MILISECONDS } from 'app/appConstants';
import { MprcContainer, MprcJoinComponents, MprcRequestErrorReport, MprcRequestIndicator } from 'components';
import { ClassroomMeetingAttendanceResponse } from 'generated/CoreApi_v1';
import { Formaters } from "lib/Formaters";
import moment from 'moment';
import { useCoreApi } from 'providers/CoreApiProvider';
import { QRCodeCanvas } from 'qrcode.react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from "react-i18next";
import { useParams } from 'react-router';
import { LmsPaths } from 'routes/LmsRouter';
import { SessionService } from 'services/sessionService';

////////////////////////////////////////////////////////////////////////////

export const MeetingAttendanceList: React.FC = () => {

    const api = useCoreApi();
    const { t } = useTranslation("lms");
    const { classroomId, componentId } = useParams();

    const [loading, setLoading] = useState<boolean>(false);
    const [viewData, setViewData] = useState<ClassroomMeetingAttendanceResponse>();
    const [viewError, setViewError] = useState<any>();

    const eventRegistrationUrl = useMemo(() => window.location.protocol + "//" + window.location.host + "/" + LmsPaths.LmsEventRegistration + "?cid=" + componentId, [componentId]);

    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        setLoading(true);
        api.v1.classroomManagement.getMeetingAttendanceData({
            classroomId: classroomId!,
            componentId: componentId!,
            isQrCodeAttendance: false
        })
            .then(response => {
                setViewData(response.data);
            })
            .catch(e => setViewError(e))
            .finally(() => setLoading(false));
    }, []);

    ////////////////////////////////////////////////////////////////////////////

    const diffDays = viewData ? (viewData.qrCodeEndDate.getTime() - viewData.qrCodeStartDate.getTime()) / ONE_DAY_MILISECONDS : 0;

    return (
        <>
            <MprcRequestIndicator visible={loading} />

            {
                viewError
                    ? <MprcRequestErrorReport error={viewError} />
                    : !loading &&
                    <>
                        <MprcContainer>
                            <Stack alignItems="end" sx={theme => ({ backgroundColor: theme.palette.notice.main, py: 1, px: 3 })} className="no-print">
                                <Link variant="body1" textTransform="uppercase" onClick={() => window.print()}>{t("Meeting.AttendanceList.PrintButtonText")}</Link>
                            </Stack>
                        </MprcContainer>

                        <Box sx={{ margin: "0 auto", width: 1024 }}>
                            {
                                viewData &&
                                <>
                                    <MprcContainer>
                                        <Box sx={{ p: 2 }}>
                                            <MprcContainer>
                                                <Stack direction="row" sx={{ border: 1, borderRadius: 2, p: 2 }} justifyContent="space-between">
                                                    <div>
                                                        <MprcContainer>
                                                            <Typography variant="h2" color="muted.dark">{viewData.meetingName}</Typography>
                                                            <Typography variant="subtitle1" component="p">
                                                                <MprcJoinComponents
                                                                    separator="&bull;"
                                                                    components={[
                                                                        viewData.trainingName,
                                                                        viewData.multipleClasses ? viewData.className : null
                                                                    ]}
                                                                />
                                                            </Typography>
                                                        </MprcContainer>
                                                        <MprcContainer>
                                                            <Typography variant="h5" color="success.main">
                                                                {
                                                                    diffDays > 1
                                                                        ? Formaters.performaFullDateRange(viewData.startDate, viewData.endDate, { dayOfWeek: true, longNames: false, replaceClosestDays: false, preposition: "always" })
                                                                            .split(' ').map((w, i) => (<>{w} {i == 3 ? <br /> : " "}</>))
                                                                        : <>
                                                                            {Formaters.performaDate(viewData.startDate, { dayOfWeek: true, longNames: true, replaceClosestDays: false, preposition: "none", excludeTime: true })}
                                                                            <br />
                                                                            {moment(viewData.startDate).format("HH:mm")} - {moment(viewData.endDate).format("HH:mm")}
                                                                        </>
                                                                }
                                                            </Typography>
                                                        </MprcContainer>
                                                        {(viewData.instructors.length > 0 || viewData.assistants.length > 0) &&
                                                            <MprcContainer>
                                                                <Typography variant="overline">{t("Meeting.Common.InstructorsText")}</Typography>
                                                                <Typography variant="body2" component="p">
                                                                    <MprcJoinComponents
                                                                        separatorVariant="comma"
                                                                        components={[...viewData.instructors, ...viewData.assistants]}
                                                                    />
                                                                </Typography>
                                                            </MprcContainer>
                                                        }
                                                        <Typography variant="body2" component="p">
                                                            {t("Meeting.AttendanceList.NoticeTextL1")} <br />
                                                            {t("Meeting.AttendanceList.NoticeTextL2")} <br />
                                                        </Typography>
                                                        <Typography variant="overline" color="warning.main">
                                                            {
                                                                diffDays > 1
                                                                    ? t("Meeting.QrCode.ValidityText_other", {
                                                                        range: Formaters.performaFullDateRange(viewData.qrCodeStartDate, viewData.qrCodeEndDate, { dayOfWeek: false, longNames: false, replaceClosestDays: false })
                                                                    })
                                                                    : t("Meeting.QrCode.ValidityText_one", {
                                                                        range: [
                                                                            t("shared:Literal.FromTime"),
                                                                            moment(viewData.qrCodeStartDate).format("HH:mm"),
                                                                            t("shared:Literal.ToTime"),
                                                                            moment(viewData.qrCodeEndDate).format("HH:mm")
                                                                        ].join(" ")
                                                                    })
                                                            }
                                                        </Typography>
                                                    </div>
                                                    <Stack direction="column">
                                                        <Avatar
                                                            src={viewData.logoId
                                                                ? SessionService.current.getAssetUrl(viewData.logoId)
                                                                : SessionService.current.defaultImages.logoUrl}
                                                            sx={{ width: 150, height: 50 }}
                                                            variant="square"
                                                        />
                                                        <Box flexGrow={1} alignContent="end">
                                                            <QRCodeCanvas
                                                                size={150}
                                                                value={eventRegistrationUrl}
                                                            />
                                                        </Box>
                                                    </Stack>
                                                </Stack>
                                            </MprcContainer>
                                            <MprcContainer>
                                                <MprcContainer>
                                                    <Typography variant="h2" color="muted.dark">{t("Meeting.AttendanceList.Title")}</Typography>
                                                </MprcContainer>
                                                <MprcContainer>
                                                    <Grid container direction="row" alignItems="start" columnSpacing={4} rowSpacing={1}>
                                                        <Grid size={{ xs: 8 }}><Typography variant="h4" color="muted.dark">{t("Meeting.AttendanceList.NameText")}</Typography></Grid>
                                                        <Grid size={{ xs: 4 }}><Typography variant="h4" color="muted.dark">{t("Meeting.AttendanceList.SignatureText")}</Typography></Grid>

                                                        {viewData.students.map((name, index) =>
                                                            <React.Fragment key={index}>
                                                                <Grid size={{ xs: 8 }}>
                                                                    <Typography variant="body2" color="muted.dark" sx={{ borderBottom: 1, py: 1 }}>
                                                                        {name}
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid size={{ xs: 4 }}>
                                                                    <Typography variant="body2" color="muted.dark" sx={{ borderBottom: 1, py: 1 }}>
                                                                        &nbsp;
                                                                    </Typography></Grid>
                                                            </React.Fragment>
                                                        )}
                                                    </Grid>
                                                </MprcContainer>
                                            </MprcContainer>
                                        </Box>
                                    </MprcContainer>
                                </>
                            }
                        </Box >
                    </>
            }
        </>
    );
};
